import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BookOpen, ChevronDown, ChevronRight } from "lucide-react";
import "./ProjectReport.css";

const chapters = [
  {
    id: 1, title: "Abstract",
    content: `Medico.in is a real-time, IoT-based healthcare monitoring platform designed to bridge the gap between patients, doctors, and caregivers using smart sensor technology and cloud computing. The platform continuously collects vital signs — heart rate, SpO2, ECG waveforms, and body temperature — from medical-grade IoT sensors connected to an ESP32 microcontroller.

Data is transmitted wirelessly to a Firebase cloud backend where it is processed and displayed on a dynamic clinical dashboard accessible to patients and healthcare professionals in real time. The system supports four user roles: Patient, Doctor, Caregiver, and Admin — each with a dedicated role-specific dashboard.

Automated alert logic triggers notifications when vital signs exceed safe thresholds, enabling proactive emergency response. Built with React.js frontend, Firebase Realtime Database backend, the platform delivers a premium hospital-grade user experience.`,
    badge: "01"
  },
  {
    id: 2, title: "Introduction",
    content: `Healthcare in the 21st century faces a critical challenge: the gap between when a patient needs medical attention and when a doctor becomes aware. Traditional monitoring systems are confined to hospitals, expensive, and inaccessible to the majority of patients — especially in rural or semi-urban areas.

The Internet of Things (IoT) offers a powerful solution. Affordable, medical-grade sensors can now measure clinical vital signs with accuracy rivalling hospital equipment. When combined with cloud computing and modern web technologies, these sensors can stream live health data to doctors anywhere in the world, at any time.

**Objectives:**
- Design and build a wearable IoT sensor suite for continuous vital sign capture
- Develop a real-time cloud pipeline using Firebase for data transmission
- Build a multi-role clinical dashboard (Patient, Doctor, Caregiver, Admin)
- Implement automated alert logic for critical vital thresholds
- Ensure data security through role-based access control`,
    badge: "02"
  },
  {
    id: 3, title: "Problem Statement",
    content: `**Problem 1: Delayed Medical Response**
Patients with chronic conditions often experience deterioration between doctor visits. Without continuous monitoring, critical events go undetected until too late.

**Problem 2: No Remote Visibility for Doctors**
Specialists lack real-time access to patient data outside scheduled appointments. Decisions are made based on outdated readings.

**Problem 3: Disconnected Caregiving**
Family members and caregivers have no structured system to monitor health remotely — relying on phone calls or manual checks.

**Problem 4: High Cost of Existing Solutions**
Commercial remote patient monitoring systems cost thousands of dollars and require proprietary hardware — inaccessible to most patients.

**Proposed Solution:**
Medico.in addresses all problems through affordable IoT hardware (ESP32 + sensors < ₹1500), real-time Firebase sync (<200ms), unified multi-role dashboards, automated alert engine, and centralized medical records.`,
    badge: "03"
  },
  {
    id: 4, title: "System Architecture",
    content: `The Medico.in system is structured in three primary layers:

**LAYER 1 — HARDWARE**
Sensors → ESP32 Microcontroller → WiFi → Internet

**LAYER 2 — CLOUD BACKEND**
Firebase Auth → Realtime Database → Cloud Functions → Alert Engine

**LAYER 3 — FRONTEND**
React.js Dashboard → Role-Based UI → Charts & Alerts

**Data Flow:**
1. ESP32 reads sensor values every 1 second
2. Data pushed to Firebase Realtime Database via REST API
3. Firebase Cloud Functions monitor thresholds and trigger alerts
4. React frontend subscribes to live listeners — updates in 50–200ms
5. Security Rules enforce role-based data access per user

**Performance Targets:**
- End-to-End Latency: <200ms
- System Uptime: 99.9%
- Data Encryption: AES-256 / TLS 1.3`,
    badge: "04"
  },
  {
    id: 5, title: "Hardware Components",
    content: `**ESP32 — Clinical Compute Node**
Dual-core Tensilica LX6 @ 240MHz. Built-in WiFi 802.11 b/g/n + BLE 4.2. Hardware AES/SHA security. Cost: ~₹350.

**MAX30102 — Heart Rate & SpO2 Sensor**
Photoplethysmography (PPG) sensor. 18-bit ADC resolution. 50–3200 samples/second. I²C interface. Measures BPM (60–100 normal) and SpO2 (95–100% normal).
Alert: SpO2 < 92% → Critical. HR > 150 → Critical.

**AD8232 — ECG Cardiac Monitor**
Single-lead ECG front-end. Captures P-wave, QRS complex, T-wave. 80dB CMRR. High-pass filter at 0.5Hz. 3 chest electrode pads.

**DS18B20 — Thermal Monitoring Node**
1-Wire digital temperature sensor. ±0.5°C accuracy. 12-bit resolution. Range: -55°C to +125°C.
Alert: Temperature > 38.5°C → Fever Warning. > 39.5°C → Critical.

**Circuit Connections:**
GPIO 21/22 → MAX30102 (I²C)
GPIO 34 (ADC) → AD8232 Output
GPIO 4 → DS18B20 Data Pin`,
    badge: "05"
  },
  {
    id: 6, title: "Software Architecture",
    content: `**Frontend — React.js 18**
Component-based architecture with React Router v6. Libraries: Framer Motion (animations), Recharts (live charts), Lucide React (icons), Firebase SDK v9.

**Backend — Firebase (BaaS)**
Firebase Auth (email/password + email verification), Realtime Database (live sensor stream), Cloud Firestore (profiles + records), Cloud Functions (alert engine), Firebase Hosting (deployment).

**Authentication Flow:**
User → Firebase Auth → Email Verification Gate → Role Fetch from Firestore → Role-specific Dashboard redirect.

**Project Structure:**
src/components/ — Shared Navigation, Footer, UI widgets
src/context/ — AuthContext, ToastContext providers
src/dashboard/ — patient/, doctor/, admin/, caregiver/ dashboards
src/pages/ — All public-facing pages
src/styles/ — Global CSS design system

**Design System:** "Sovereign Tactical HUD" — dark theme (navy/black), cyan/blue/green/orange accents, glassmorphism cards, hex grid backgrounds, scanline overlays.`,
    badge: "06"
  },
  {
    id: 7, title: "Features & Modules",
    content: `**Patient Dashboard:** Live vitals ticker, 24-hour trend charts, alert history, appointment booking, medical records upload, device settings.

**Doctor Dashboard:** Patient registry with live status, patient detail views, real-time alert feed, appointment management, clinical notes.

**Caregiver Dashboard:** Single-patient monitoring focus, simplified vitals layout, push-style alerts, quick doctor contact.

**Admin Dashboard:** Full user management, doctor–patient assignment, system analytics, hospital registry.

**Alert Engine — 3 Levels:**
Level 1 INFO — Slightly outside normal range → Yellow indicator
Level 2 WARNING — Significantly outside range → Toast notification
Level 3 CRITICAL — Dangerous range → Full-screen alert + doctor notification + event log

**Vital Alert Thresholds:**
Heart Rate: Normal 60–100 BPM | Warning <50 or >120 | Critical <40 or >150
SpO2: Normal 95–100% | Warning 92–94% | Critical <92%
Temperature: Normal 36–37.5°C | Warning 37.5–38.5°C | Critical >38.5°C`,
    badge: "07"
  },
  {
    id: 8, title: "Database & Security",
    content: `**Firebase Realtime Database Structure:**
/vitals/{patientId} → heartRate, spo2, temperature, ecg, timestamp
/alerts/{patientId} → type, vital, value, timestamp

**Firestore Collections:**
users/ → name, email, role, phone, assignedDoctor
patients/ → profile, medicalHistory, assignedDoctor
appointments/ → patientId, doctorId, date, time, status, notes
records/ → patientId, type, fileUrl, uploadedAt

**Security Architecture:**
- Firebase Auth with mandatory email verification
- Role-Based Access Control (4 roles: patient, doctor, caregiver, admin)
- AES-256 encryption for all data in transit (HTTPS/TLS 1.3)
- Service Account Isolation — ESP32 uses limited-privilege account
- Data Sovereignty — patients own their data and control access
- Security Rules enforce strict read/write permissions per role
- HIPAA-aligned data handling practices`,
    badge: "08"
  },
  {
    id: 9, title: "Testing & Results",
    content: `**MAX30102 Heart Rate Accuracy:**
Average accuracy across 3 test subjects: 98.7% (vs. certified medical oximeter)

**SpO2 Accuracy:**
Average accuracy: 99.3% (within ±1% of reference device)

**DS18B20 Temperature:**
Deviation: ±0.1°C vs. mercury thermometer — within manufacturer spec.

**Software Performance:**
Firebase write → Dashboard update: ~85ms average
Alert trigger latency: <200ms
Page load time (first paint): 1.2 seconds
Concurrent user sessions tested: 50+
30-day uptime: 99.8%

**User Acceptance Testing (UAT):**
Patient role — 100% task pass rate
Doctor role — 100% task pass rate
Admin role — 95% task pass rate
Caregiver role — 100% task pass rate

**Known Limitations:**
ECG quality affected by motion artifacts. MAX30102 accuracy depends on correct fingertip placement. Firebase free tier limited to 1GB storage.`,
    badge: "09"
  },
  {
    id: 10, title: "Team & Contributions",
    content: `**Vivek** — B.Tech CSE (Blockchain)
Role: Full Stack Developer, Lead Software Architect
Designed entire platform architecture, Firebase schema, React component system, authentication flow, routing, global design system. Built all dashboards and implemented real-time alert engine.

**Vikash Verma** — B.Tech CSE (IIoT)
Role: IoT Engineer, Project Lead, Report Maker
Managed project timelines. Led hardware integration of all sensors with ESP32. Wrote firmware for data transmission. Supervised circuit assembly and project report structure.

**Digambar Singh** — B.Tech CSE
Role: UI/UX Designer
Designed the Sovereign Tactical HUD design system. Created wireframes and mockups for all dashboards. Defined colour palette, typography, and glassmorphism component library.

**Durgesh Kumar** — B.Tech CSE
Role: Backend Engineer
Built Firebase Cloud Functions for alert processing and data validation. Designed Firestore data schema and wrote security rules.

**Anil Kumar** — B.Tech CSE
Role: Backend Engineer, Research Paper Author
Implemented authentication logic and role-based routing. Authored the final research paper and project documentation.

**Vedant Sharma** — B.Tech CSE (IIoT)
Role: IoT Device Engineer
Handled sensor procurement, circuit wiring, and calibration. Implemented MAX30102 and DS18B20 sensor libraries. Tested sensor accuracy against certified medical devices.`,
    badge: "10"
  },
  {
    id: 11, title: "Conclusion & Future Scope",
    content: `**Conclusion:**
Medico.in successfully demonstrates that affordable IoT hardware combined with a robust cloud backend and premium clinical interface can deliver real-time health monitoring previously available only in hospital settings.

Key achievements:
✓ Sub-200ms end-to-end telemetry latency
✓ 98.7% heart rate measurement accuracy
✓ Four fully functional role-based dashboards
✓ Secure HIPAA-aligned data architecture
✓ Premium accessible UI for clinical and general users

**Future Scope:**
SMS & WhatsApp Critical Alerts — Q3 2026 (Twilio API)
AI-powered anomaly detection — Q4 2026 (TensorFlow.js)
Telemedicine video consultation — Q4 2026 (WebRTC)
Wearable wristband form factor — 2027 (Custom PCB + BLE)
Blockchain health record audit trail — 2027 (Polygon)
Multi-language support (Hindi, Tamil) — Q3 2026
FDA/CE medical device certification pathway — 2027

**Social Impact:**
With hardware cost under ₹1500, Medico.in is accessible to low-income households. A single doctor could remotely monitor hundreds of rural patients simultaneously — fundamentally changing healthcare access in India and beyond.`,
    badge: "11"
  }
];

export default function ProjectReport() {
  const [open, setOpen] = useState(1);

  const toggle = (id) => setOpen(open === id ? null : id);

  return (
    <div className="pr-shell">
      <div className="pr-grid-bg" />

      {/* TOP BAR */}
      <header className="pr-topbar">
        <Link to="/" className="pr-back">
          <ArrowLeft size={16} />
          <span>Back to Medico.in</span>
        </Link>
        <div className="pr-topbar-center">
          <BookOpen size={16} />
          <span>PROJECT REPORT — MEDICO.IN</span>
        </div>
        <a
          href="/Medico_Project_Report.md"
          download
          className="pr-download"
        >
          Download .MD
        </a>
      </header>

      <div className="pr-layout">
        {/* SIDEBAR */}
        <nav className="pr-sidebar">
          <div className="pr-sidebar-header">CONTENTS</div>
          {chapters.map(ch => (
            <button
              key={ch.id}
              className={`pr-nav-item ${open === ch.id ? "active" : ""}`}
              onClick={() => toggle(ch.id)}
            >
              <span className="pr-nav-num">{ch.badge}</span>
              <span className="pr-nav-label">{ch.title}</span>
            </button>
          ))}
        </nav>

        {/* CONTENT */}
        <main className="pr-content">
          {/* Cover */}
          <div className="pr-cover">
            <div className="pr-cover-badge">B.TECH ENGINEERING PROJECT — 2026</div>
            <h1 className="pr-cover-title">Medico<span className="pr-dot">.in</span></h1>
            <p className="pr-cover-sub">Real-Time IoT-Based Healthcare Monitoring System</p>
            <div className="pr-cover-meta">
              <span>Version 2.0</span>
              <span className="pr-sep">·</span>
              <span>May 2026</span>
              <span className="pr-sep">·</span>
              <span>6 Members</span>
              <span className="pr-sep">·</span>
              <span>11 Chapters</span>
            </div>
          </div>

          {/* Chapters */}
          {chapters.map(ch => (
            <div key={ch.id} className={`pr-chapter ${open === ch.id ? "open" : ""}`}>
              <button className="pr-chapter-header" onClick={() => toggle(ch.id)}>
                <div className="pr-chapter-left">
                  <span className="pr-chapter-num">CHAPTER {ch.badge}</span>
                  <h2 className="pr-chapter-title">{ch.title}</h2>
                </div>
                {open === ch.id
                  ? <ChevronDown size={18} className="pr-chevron" />
                  : <ChevronRight size={18} className="pr-chevron" />}
              </button>
              {open === ch.id && (
                <div className="pr-chapter-body">
                  {ch.content.split("\n\n").map((para, i) => (
                    <p key={i} className={para.startsWith("**") ? "pr-bold-para" : "pr-para"}>
                      {para.split(/(\*\*[^*]+\*\*)/).map((part, j) =>
                        part.startsWith("**") && part.endsWith("**")
                          ? <strong key={j}>{part.slice(2, -2)}</strong>
                          : part
                      )}
                    </p>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Footer */}
          <div className="pr-report-footer">
            <span>© 2026 Team Medico.in — All Rights Reserved</span>
            <span className="pr-sep">·</span>
            <span>B.Tech Engineering Project Report</span>
          </div>
        </main>
      </div>
    </div>
  );
}
